//@flow
import React from 'react';
import type {ConversationMessage} from '../../../../../../../entities';
import {makeStyles, IconButton, Typography} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import AudiotrackIcon from '@material-ui/icons/Audiotrack';
import VideocamIcon from '@material-ui/icons/Videocam';
import {colors} from '../../../../../../../assets/styles/colors';
import ImageRenderer from '../../../../../../../elements/ImageRenderer';

type Props = {
  message: ConversationMessage,
  onClear: () => void,
};

const useStyles = makeStyles(theme => ({
  replyingMessageWrapper: {
    padding: '.25rem',
  },
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1),
    borderLeft: `4px solid ${colors.primary}`,
    backgroundColor: colors.grey200,
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    marginBottom: theme.spacing(1),
  },
  content: {
    flexGrow: 1,
    paddingRight: theme.spacing(4),
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  messageContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  mediaPreview: {
    width: '48px !important',
    height: '48px !important',
    objectFit: 'cover',
    borderRadius: '.25rem',
  },
  caption: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  typeIcon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
  },
}));

export default function ReplyingMessage({message, onClear}: Props) {
  const classes = useStyles();

  const renderContent = () => {
    if (message.images?.length) {
      return (
        <div className={classes.messageContainer}>
          {message.images[0].src && (
            <ImageRenderer
              src={message.images[0].src}
              alt="attachment"
              className={classes.mediaPreview}
            />
          )}
          {message.text && (
            <Typography variant="body2" className={classes.caption}>
              {message.text}
            </Typography>
          )}
        </div>
      );
    }
    if (message.videos?.length) {
      return (
        <div className={classes.messageContainer}>
          <VideocamIcon className={classes.typeIcon} fontSize="large" />
          {message.text && (
            <Typography variant="body2" className={classes.caption}>
              {message.text}
            </Typography>
          )}
        </div>
      );
    }
    if (message.audios?.length) {
      return (
        <div className={classes.messageContainer}>
          <AudiotrackIcon className={classes.typeIcon} fontSize="large" />
          {message.text && (
            <Typography variant="body2" className={classes.caption}>
              {message.text}
            </Typography>
          )}
        </div>
      );
    }
    if (message.documents?.length) {
      return (
        <div className={classes.messageContainer}>
          <InsertDriveFileIcon className={classes.typeIcon} fontSize="large" />
          {message.text && (
            <Typography variant="body2" className={classes.caption}>
              {message.text}
            </Typography>
          )}
        </div>
      );
    }
    if (message.text) {
      return <Typography variant="body2">{message.text}</Typography>;
    }
    return <Typography variant="body2">[Empty message]</Typography>;
  };

  return (
    <div className={classes.replyingMessageWrapper}>
      <div className={classes.container}>
        <div className={classes.content}>{renderContent()}</div>
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={onClear}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
}
